import { useState, useMemo, forwardRef, useImperativeHandle, useCallback, Fragment } from 'react';
import { useTranslation } from 'react-i18next';

// Layout constants matching the design mockup
const CARD_W   = 200;
const CARD_H   = 104;
const V_GAP    = 16;
const ROW      = CARD_H + V_GAP;
const H_GAP    = 80;
const COL_STEP = CARD_W + H_GAP;
const PAD_X    = 4;
const PAD_Y    = 36;
const BTN_R    = 14;

const COL_COLORS = ['var(--vo-cobalto)', 'var(--vo-purple)', 'var(--vo-celeste)', 'var(--vo-success)'];

const lx = (ci) => ci * COL_STEP + PAD_X;
const ty = (ri) => ri * ROW + PAD_Y;
const cy = (ri) => ty(ri) + CARD_H / 2;

const critColor = (c) => {
  if (!c) return 'var(--vo-steel)';
  const v = String(c).toLowerCase();
  if (v.includes('baixa') || v.includes('low') || v.includes('baja')) return 'var(--vo-success)';
  if (v.includes('muito') || v.includes('very') || v.includes('critica')) return 'var(--vo-danger)';
  if (v.includes('alta') || v.includes('high')) return 'var(--vo-sev-high)';
  return 'var(--vo-warning)';
};

const Graphic = forwardRef(function Graphic({ areas, processes, systems, assets, onSelectionChange }, ref) {
  const { t } = useTranslation(['mapping']);

  const [expanded, setExpanded] = useState(new Set());
  const [selected, setSelected] = useState(null);
  const [zoom, setZoom]         = useState(1);

  const toggle = useCallback((id) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const select = useCallback((id) => {
    setSelected(prev => {
      const next = prev === id ? null : id;
      onSelectionChange?.(next);
      return next;
    });
  }, [onSelectionChange]);

  const processById = useMemo(() => new Map((processes || []).map(p => [p._id, p])), [processes]);
  const systemById  = useMemo(() => new Map((systems  || []).map(s => [s._id, s])), [systems]);
  const assetById   = useMemo(() => new Map((assets   || []).map(a => [a._id, a])), [assets]);

  // Column 0: all areas
  const col0 = useMemo(() => areas || [], [areas]);

  // Column 1: processes from expanded areas (deduplicated)
  const col1 = useMemo(() => {
    const seen = new Set();
    const out  = [];
    col0.forEach(area => {
      if (!expanded.has(area._id)) return;
      (area.processes || []).forEach(p => {
        const id = p._id;
        if (seen.has(id)) return;
        seen.add(id);
        out.push({ ...p, ...(processById.get(id) || {}) });
      });
    });
    return out;
  }, [col0, expanded, processById]);

  // Column 2: systems from expanded processes in col1
  const col2 = useMemo(() => {
    const seen = new Set();
    const out  = [];
    col1.forEach(proc => {
      if (!expanded.has(proc._id)) return;
      const full = processById.get(proc._id) || proc;
      (full.systems || proc.systems || []).forEach(s => {
        const id = s._id || s;
        if (seen.has(id)) return;
        seen.add(id);
        out.push(systemById.get(id) || (typeof s === 'object' ? s : { _id: id, name: id }));
      });
    });
    return out;
  }, [col1, expanded, processById, systemById]);

  // Column 3: assets from expanded systems in col2
  const col3 = useMemo(() => {
    const seen = new Set();
    const out  = [];
    col2.forEach(sys => {
      if (!expanded.has(sys._id)) return;
      const full = systemById.get(sys._id) || sys;
      (full.assets || sys.assets || []).forEach(a => {
        const id = a._id || a;
        if (seen.has(id)) return;
        seen.add(id);
        out.push(assetById.get(id) || (typeof a === 'object' ? a : { _id: id, name: id }));
      });
    });
    return out;
  }, [col2, expanded, systemById, assetById]);

  // SVG cubic Bezier paths between adjacent columns
  const paths = useMemo(() => {
    const out = [];

    col0.forEach((area, ai) => {
      if (!expanded.has(area._id)) return;
      (area.processes || []).forEach(proc => {
        const bi = col1.findIndex(p => p._id === proc._id);
        if (bi < 0) return;
        const x1 = lx(0) + CARD_W, y1 = cy(ai);
        const x2 = lx(1),          y2 = cy(bi);
        const mx = (x1 + x2) / 2;
        out.push({ key: `a${area._id}-p${proc._id}`, d: `M ${x1} ${y1} C ${mx} ${y1} ${mx} ${y2} ${x2} ${y2}` });
      });
    });

    col1.forEach((proc, pi) => {
      if (!expanded.has(proc._id)) return;
      const full = processById.get(proc._id) || proc;
      (full.systems || proc.systems || []).forEach(sys => {
        const id = sys._id || sys;
        const si = col2.findIndex(s => s._id === id);
        if (si < 0) return;
        const x1 = lx(1) + CARD_W, y1 = cy(pi);
        const x2 = lx(2),          y2 = cy(si);
        const mx = (x1 + x2) / 2;
        out.push({ key: `p${proc._id}-s${id}`, d: `M ${x1} ${y1} C ${mx} ${y1} ${mx} ${y2} ${x2} ${y2}` });
      });
    });

    col2.forEach((sys, si) => {
      if (!expanded.has(sys._id)) return;
      const full = systemById.get(sys._id) || sys;
      (full.assets || sys.assets || []).forEach(asset => {
        const id = asset._id || asset;
        const ai = col3.findIndex(a => a._id === id);
        if (ai < 0) return;
        const x1 = lx(2) + CARD_W, y1 = cy(si);
        const x2 = lx(3),          y2 = cy(ai);
        const mx = (x1 + x2) / 2;
        out.push({ key: `s${sys._id}-a${id}`, d: `M ${x1} ${y1} C ${mx} ${y1} ${mx} ${y2} ${x2} ${y2}` });
      });
    });

    return out;
  }, [col0, col1, col2, col3, expanded, processById, systemById]);

  const colData = [col0, col1, col2, col3];
  const maxRows = Math.max(...colData.map(c => c.length), 1);
  const lastVisibleCol = colData.reduceRight((acc, col, ci) => acc === -1 && col.length > 0 ? ci : acc, -1);
  const visibleColCount = Math.max(lastVisibleCol + 1, 1);
  const totalW  = lx(visibleColCount - 1) + CARD_W + PAD_X + 20;
  const totalH  = ty(maxRows) + 20;

  const hasChildren = useCallback((entity, ci) => {
    if (ci === 0) return ((entity.processes || []).length + (entity.systems || []).length + (entity.assets || []).length) > 0;
    if (ci === 1) {
      const full = processById.get(entity._id) || entity;
      return (full.systems || entity.systems || []).length > 0;
    }
    if (ci === 2) {
      const full = systemById.get(entity._id) || entity;
      return (full.assets || entity.assets || []).length > 0;
    }
    return false;
  }, [processById, systemById]);

  useImperativeHandle(ref, () => ({
    expandAll: () => {
      const all = new Set();
      (areas     || []).forEach(a => all.add(a._id));
      (processes || []).forEach(p => all.add(p._id));
      (systems   || []).forEach(s => all.add(s._id));
      setExpanded(all);
    },
    collapseAll: () => setExpanded(new Set()),
    clearSelection: () => {
      setSelected(null);
      onSelectionChange?.(null);
    },
    zoomIn:  () => setZoom(z => Math.min(+(z + 0.15).toFixed(2), 2.0)),
    zoomOut: () => setZoom(z => Math.max(+(z - 0.15).toFixed(2), 0.4)),
  }), [areas, processes, systems, onSelectionChange]);

  const colHeaders = [
    t('mapping:visualization.legend.areas'),
    t('mapping:visualization.legend.processes'),
    t('mapping:visualization.legend.systems'),
    t('mapping:visualization.legend.assets'),
  ];

  const cardTypes = [
    t('mapping:nodeTypes.area'),
    t('mapping:nodeTypes.process'),
    t('mapping:nodeTypes.system'),
    t('mapping:nodeTypes.asset'),
  ];

  const critLabel = t('mapping:fields.criticity').replace(':', '');
  const noItems   = t('mapping:visualization.noItems');

  return (
    // Outer div sized with zoom so the scroll area tracks correctly
    <div style={{ width: totalW * zoom, height: totalH * zoom, position: 'relative', flexShrink: 0 }}>
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: totalW,
        height: totalH,
        transform: `scale(${zoom})`,
        transformOrigin: 'top left',
      }}>
        {/* SVG connection lines */}
        <svg
          style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none', overflow: 'visible' }}
          width={totalW}
          height={totalH}
        >
          {paths.map(p => (
            <path
              key={p.key}
              d={p.d}
              fill="none"
              stroke="var(--vo-celeste)"
              strokeWidth={1.5}
              strokeLinecap="round"
              opacity={0.45}
            />
          ))}
        </svg>

        {/* Column headers — col0 always; col1-3 only when they have cards */}
        {colHeaders.map((label, ci) => (ci > 0 && colData[ci].length === 0 ? null :
          <div
            key={`hdr-${ci}`}
            style={{
              position: 'absolute',
              left: lx(ci),
              top: 6,
              width: CARD_W,
              fontFamily: 'Maven Pro,sans-serif',
              fontSize: 10,
              fontWeight: 700,
              color: COL_COLORS[ci],
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              whiteSpace: 'nowrap',
            }}
          >
            {label} ({colData[ci].length})
          </div>
        ))}

        {/* Cards and expand/collapse buttons */}
        {colData.flatMap((col, ci) =>
          col.length > 0
            ? col.map((entity, ri) => {
                const cc     = critColor(entity.criticity);
                const color  = COL_COLORS[ci];
                const isExp  = expanded.has(entity._id);
                const isSel  = selected === entity._id;
                const hasKid = hasChildren(entity, ci);

                return (
                  <Fragment key={`f-${ci}-${entity._id || ri}`}>
                    <div
                      onClick={() => select(entity._id)}
                      style={{
                        position: 'absolute',
                        left: lx(ci),
                        top: ty(ri),
                        width: CARD_W,
                        height: CARD_H,
                        background: isSel ? 'var(--vo-capri-50)' : isExp ? 'var(--vo-capri-25)' : 'white',
                        border: `1.5px solid ${isSel ? 'var(--vo-celeste)' : isExp ? 'var(--vo-capri-200)' : 'var(--vo-cloud)'}`,
                        borderRadius: 12,
                        padding: '14px 16px 12px',
                        boxSizing: 'border-box',
                        boxShadow: isSel
                          ? '0 4px 14px color-mix(in srgb, var(--vo-info) 22%, transparent)'
                          : isExp
                          ? '0 2px 8px var(--vo-info-10)'
                          : '0 1px 4px color-mix(in srgb, var(--vo-escuro) 7%, transparent)',
                        transition: 'all 140ms ease',
                        userSelect: 'none',
                        cursor: 'pointer',
                        zIndex: isSel ? 5 : 1,
                      }}
                    >
                      <p style={{ fontFamily: 'Rubik,sans-serif', fontSize: 13, fontWeight: 700, color: 'var(--vo-ink)', margin: '0 0 2px', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {entity.name || '—'}
                      </p>
                      <p style={{ fontFamily: 'Maven Pro,sans-serif', fontSize: 10, color, fontWeight: 600, margin: 0, letterSpacing: '0.03em' }}>
                        {cardTypes[ci]}
                      </p>
                      <div style={{ height: 1, background: 'var(--vo-mist)', margin: '10px 0' }} />
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                        <span style={{ fontFamily: 'Lato,sans-serif', fontSize: 11, color: 'var(--vo-steel)', flex: 1 }}>
                          {critLabel}
                        </span>
                        <span style={{ width: 9, height: 9, borderRadius: '50%', background: cc, flexShrink: 0, display: 'inline-block' }} />
                        <span style={{ fontFamily: 'Maven Pro,sans-serif', fontSize: 12, fontWeight: 700, color: cc }}>
                          {entity.criticity || '—'}
                        </span>
                      </div>
                    </div>

                    {hasKid && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); toggle(entity._id); }}
                        style={{
                          position: 'absolute',
                          left: lx(ci) + CARD_W - BTN_R,
                          top: ty(ri) + CARD_H / 2 - BTN_R,
                          width: BTN_R * 2,
                          height: BTN_R * 2,
                          borderRadius: '50%',
                          background: isExp
                            ? 'linear-gradient(135deg,var(--vo-success),color-mix(in srgb, var(--vo-success) 80%, white))'
                            : 'linear-gradient(135deg,var(--vo-cobalto),var(--vo-celeste))',
                          border: '2.5px solid white',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 2px 6px color-mix(in srgb, var(--vo-escuro) 20%, transparent)',
                          zIndex: 10,
                          transition: 'background 150ms ease',
                          padding: 0,
                        }}
                      >
                        {isExp
                          ? <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round"><line x1="5" y1="12" x2="19" y2="12" /></svg>
                          : <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
                        }
                      </button>
                    )}
                  </Fragment>
                );
              })
            : ci === 0
              ? [
                  <div
                    key={`empty-${ci}`}
                    style={{
                      position: 'absolute',
                      left: lx(ci),
                      top: ty(0),
                      width: CARD_W,
                      height: CARD_H,
                      border: '1.5px dashed var(--vo-line)',
                      borderRadius: 12,
                      background: 'color-mix(in srgb, var(--vo-white) 60%, transparent)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <span style={{ fontFamily: 'Lato,sans-serif', fontSize: 12, color: 'var(--vo-steel)' }}>
                      {noItems}
                    </span>
                  </div>,
                ]
              : []
        )}
      </div>
    </div>
  );
});

export default Graphic;
